import { useState, useEffect, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as XCircleFill } from "bootstrap-icons/icons/x-circle-fill.svg";
import { ReactComponent as Download } from "bootstrap-icons/icons/download.svg";
import { ReactComponent as Search } from "bootstrap-icons/icons/search.svg";
// import { ReactComponent as Calendar } from "bootstrap-icons/icons/calendar.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";
import { ReactComponent as ArrowClockwise } from "bootstrap-icons/icons/arrow-clockwise.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../api/ApiServices";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import { handleError } from "../../components/utils/AuthenticationCheck";
import formatDate from "../../components/utils/DateFormatter";
import CurrencyFormatter from "../../components/utils/CurrencyFormatter";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import AxiosApiCall from "../../api/AxiosApiCall";
import { downloadCSVFromApi } from "../../components/utils/DownlaodCSVFromApi";

const GetAllOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [perPage, setPerPage] = useState(100);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOrderType, setSelectedOrderType] = useState("");
    const { permissions } = useContext(AuthContext)

    // Date filter state
    const [filters, setFilters] = useState({
        start_date: "",
        end_date: "",
    });

    const navigate = useNavigate();
    //axios api encryption call
    const axios = AxiosApiCall();

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Clear date filters
    const handleClearDates = () => {
        setFilters({
            start_date: "",
            end_date: "",
        });
        toast.success("Date filters cleared");
    };

    // Check if both dates are selected
    const areDatesSelected = filters.start_date && filters.end_date;

    // Function to format permission text
    const formatPermissionText = (text) => {
        // Replace underscores with spaces
        let formattedText = text.replace(/_/g, " ");

        // Capitalize the first letter of each word
        formattedText = formattedText
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());

        return formattedText;
    };


    const fetchAllOrdersList = async (page = 1, perPage = 100) => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.get(ApiServices.getAllOrders, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                params: {
                    page,
                    per_page: perPage
                },
                withCredentials: true,
            });

            const { status, message, data } = response.data;

            if (status === 200 && Array.isArray(data.orders)) {
                setOrders(data.orders);
                setPerPage(data.per_page);
                setTotal(data.total);
                setPage(data.current_page);
                toast.success(message, { toastId: "orders-fetch-success" });
            } else {
                handleError({ response: { status, data: { message } } }, navigate);
            }
        } catch (error) {
            handleError(error, navigate);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllOrdersList(page, perPage);
    }, [page, perPage]);

    // Handle page change - reset to page 1 when filters change
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handlePerRowsChange = (newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
    };

    // admin cancel customer order api cal here
    const submitCancellation = async (orderId, userId, reason) => {
        const formData = {
            order_id: orderId,
            reason: reason,
            user_id: userId,
        }
        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;
            if (!token) {
                throw new Error("No access token found.");
            }

            const response = await axios.post(
                ApiServices.returnCustomerOrderByAdmin,
                {
                    order_id: orderId,
                    user_id: userId,
                    reason: reason
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: true,
                }
            );

            const { status, message } = response.data;
            if (status === 200) {
                toast.success(message);
                fetchAllOrdersList();
            } else {
                toast.error(message || 'Failed to cancel order');
            }
        } catch (error) {
            handleError(error, navigate);
            toast.error('An error occurred while cancelling the order');
        }
    };

    // Handle confirmation pop-up for product update
    const handleCancelOrder = (orderId, userId) => {
        Swal.fire({
            title: 'Cancel Order',
            html: `
            <p>Are you sure you want to cancel this order?</p>
            <textarea 
                id="reason" 
                class="swal2-textarea" 
                placeholder="Enter cancellation reason..."
                required
            ></textarea>
        `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Confirm Cancellation',
            cancelButtonText: 'Cancel',
            focusConfirm: false,
            preConfirm: () => {
                const reason = Swal.getPopup().querySelector('#reason').value;
                if (!reason) {
                    Swal.showValidationMessage('Please enter a cancellation reason');
                    return false;
                }
                return { reason };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const { reason } = result.value;
                submitCancellation(orderId, userId, reason);
            }
        });
    };

    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    //selct user type
    const handleOrderTypeSelect = (type) => {
        setSelectedOrderType(type);
        setIsDropdownOpen(false);
    };

    // Define Expandable Component for Actions
    const ExpandableComponent = ({ data }) => (
        <div className="flex justify-start  relative">
            <div className="mx-2 my-2 group relative">
                {permissions.some(permission => permission.name === 'order.view') && (
                    <Link
                        to="/admin/order/details"
                        state={{ orderId: data.id }}
                        className="mx-2 group"
                    >
                        <Eye className="w-5 h-5 text-darkGreen3" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Details
                        </div>
                    </Link>
                )}
            </div>
            <div className="mx-2 my-2 group relative">
                {permissions.some(permission => permission.name === 'order.edit') && (
                    <Link
                        to="/admin/order/update"
                        state={{ orders: data }}
                        className="mx-2  group"
                    >
                        <PencilSquare className="w-5 h-5 text-dash4" />
                        <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                            Edit
                        </div>
                    </Link>
                )}
            </div>
            {permissions.some(permission => permission.name === 'order.return') && (
                <button
                    onClick={() => handleCancelOrder(data.id, data.user_id)}
                    className="mx-2 group"
                >
                    <XCircleFill className="w-5 h-5 text-dash3" />
                    <div className="absolute top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                        Cancel order
                    </div>
                </button>
            )}
        </div>
    );

    const columns = [
        {
            name: "Customer Name",
            selector: (row) => row.user_name.toUpperCase() || "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Tracking Id",
            selector: (row) => row.tracking_id ? row.tracking_id : "NOT AVIALABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Total Amount",
            selector: (row) => CurrencyFormatter(row.total_amount) || "NOT AVAILABLE",
            sortable: true,
            width: "200px",
        },
        {
            name: "Type",
            selector: (row) => formatPermissionText(row.order_type).toUpperCase() || "NOT AVAILABLE",
            sortable: false,
            width: "200px",
        },
        {
            name: "Status",
            selector: (row) => row.status ? row.status.toUpperCase() : "NOT AVAILABLE",
            sortable: true,
            cell: (row) => {
                let bgColor = "#A0AEC0";
                let textColor = "#F7F7F7";

                if (row.status === "processing") {
                    bgColor = "#003FB9";
                } else if (row.status === "cancelled") {
                    bgColor = "#CC0000";
                } else if (row.status === "completed") {
                    bgColor = "#FFA439";
                } else if (row.status === "failed") {
                    bgColor = "#CC0000";
                } else if (row.status === "shipped") {
                    bgColor = "#48BB78";
                } else if (row.status === "ongoing") {
                    bgColor = "#1E4C2F";
                }

                return (
                    <span style={{ backgroundColor: bgColor, color: textColor, padding: '5px 10px', borderRadius: '4px' }}>
                        {row.status ? row.status.toUpperCase() : "NOT AVAILABLE"}
                    </span>
                );
            },
            width: "150px",
        },
        {
            name: "Order Items",
            selector: (row) => {
                const physicalItems = row.physical_items || [];
                if (physicalItems.length > 0) {
                    return physicalItems.map(i => i.network_name.toUpperCase() || "NO NETWORK").join(", ");
                }

                const orderItems = row.order_items || [];
                if (orderItems.length > 0) {
                    return orderItems.map(i => i.stock.toUpperCase() || "NO STOCK").join(", ");
                }

                return "NOT AVAILABLE";
            },
        },
        {
            name: "Created At",
            selector: (row) => formatDate(row.created_at) || "NOT AVAILABLE",
            sortable: true,
            width: "250px",
        },
    ];

    // Apply filters
    const applyFilters = (orders) => {
        const lowerFilterText = filterText.toLowerCase();

        return orders.filter(order => {
            // Text-based filtering
            const matchesUserName = order.user_name?.toLowerCase().includes(lowerFilterText);
            const matchesCreatedAt = order.created_at?.toLowerCase().includes(lowerFilterText);
            const matchesProductOrderType = order.order_type?.toLowerCase().includes(lowerFilterText);
            const matchesTotalAmount = order.total_amount?.toString().includes(lowerFilterText);
            const matchesStatus = order.status?.toString().toLowerCase().includes(lowerFilterText);
            const matchesOrderItemsTrackingId = order.tracking_id?.toString().toLowerCase().includes(lowerFilterText);
            const matchesOrderItems = order.order_items?.some(item =>
                item.stock?.toLowerCase().includes(lowerFilterText)
            );

            // Date range filtering
            let matchesDateRange = true;
            if (areDatesSelected) {
                const orderDate = new Date(order.created_at);
                const startDate = new Date(filters.start_date);
                const endDate = new Date(filters.end_date);

                endDate.setHours(23, 59, 59, 999);
                matchesDateRange = orderDate >= startDate && orderDate <= endDate;
            }

            // Order type filtering
            const matchesOrderType = !selectedOrderType || order.status?.toLowerCase() === selectedOrderType.toLowerCase();

            return (
                (matchesUserName ||
                    matchesCreatedAt ||
                    matchesProductOrderType ||
                    matchesTotalAmount ||
                    matchesOrderItems ||
                    matchesOrderItemsTrackingId ||
                    matchesStatus) &&
                matchesOrderType &&
                matchesDateRange
            );
        });
    };

    // Handle CSV Download
    const handleDownloadCSV = async () => {
        if (!areDatesSelected) {
            toast.error("Please select both start and end dates to download CSV");
            return;
        }

        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);

        if (startDate > endDate) {
            toast.error("Start date must be before end date");
            return;
        }


        //limit date range to a year
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 365) {
          toast.error("Date range cannot exceed 1 year");
          return;
        }

        setLoadingCSV(true);

        try {
            const authData = JSON.parse(localStorage.getItem("auth"));
            const token = authData?.token;

            if (!token) {
                toast.error("No access token found");
                return;
            }

            // Prepare download payload
            const downloadPayload = {
                start_date: filters.start_date,
                end_date: filters.end_date,
                download: 1,
            };

            // Create filename with date range
            const startDateFormatted = filters.start_date.replace(/-/g, '_');
            const endDateFormatted = filters.end_date.replace(/-/g, '_');
            const filename = `orders_${startDateFormatted}_to_${endDateFormatted}.csv`;

            // Call the download function
            await downloadCSVFromApi(
                ApiServices.getAllOrders,
                downloadPayload,
                token,
                {
                    filename: filename,
                    columnsToRemove: [], // Add sensitive columns to exclude
                    maxRows: 50000
                }
            );

        } catch (error) {
            console.error("Download error:", error);
            handleError(error, navigate);
            if (error.message && error.message.includes("CSV")) {
                toast.error("Failed to process CSV file");
            } else if (error.message && error.message.includes("network")) {
                toast.error("Network error during download");
            }
        } finally {
            setLoadingCSV(false);
        }
    };

    const filteredItems = applyFilters(orders);
    const data = filteredItems.map((order) => ({
        user_name: order.user_name || "NOT AVAILABLE",
        total_amount: order.total_amount || "NOT AVAILABLE",
        status: order.status || "NOT AVAILABLE",
        created_at: order.created_at || "NOT AVAILABLE",
        order_items: order.order_items ? order.order_items.map(item => ({
            stock: item.stock || "NOT AVAILABLE",
            quantity: item.quantity || 0,
            price: item.price || "0.00",
        })) : [],
        physical_items: order.physical_items ? order.physical_items.map(item => ({
            network_name: item.network_name || "NOT AVAILABLE",
            quantity: item.quantity || 0,
            price: item.price || "0.00",
        })) : [],
        id: order.id,
        user_id: order.user_id,
        order_type: order.order_type,
        tracking_id: order.tracking_id,
    }));

    // Sort data based on creation date
    const sortedData = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return (
        <div>
            <MainLayouts>
                <div className=" sm:rounded-lg py-6 w-full">
                    <section
                        className="w-full pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Orders
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>

                    <div className="pb-4 bg-white dark:bg-gray-900">
                        {/* DESKTOP FILTERS (md and up) - All in one row */}
                        <div className="hidden md:flex flex-wrap items-center gap-3">
                            <div className="relative flex-grow min-w-[200px]">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                        placeholder="Search ..."
                                        value={filterText}
                                        onChange={handleFilter}
                                    />
                                </div>
                            </div>

                            {/* Order Status Dropdown */}
                            <div className="min-w-[150px] relative">
                                {permissions.some(permission => permission.name === 'order.view') && (
                                    <div className="relative w-full md:w-auto flex-shrink-0">
                                        <div>
                                            <button
                                                type="button"
                                                className="bg-darkGreen3 inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-3 text-sm font-semibold text-dGrey3"
                                                id="menu-button"
                                                aria-expanded={isDropdownOpen}
                                                aria-haspopup="true"
                                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                            >
                                                Select Order Status
                                                <svg
                                                    className="-mr-1 h-5 w-5 text-gray-400"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        {isDropdownOpen && (
                                            <div
                                                className="absolute right-0 z-10 mt-2 w-full md:w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-lGrey shadow-lg ring-1 ring-black1 ring-opacity-5 focus:outline-none"
                                                role="menu"
                                                aria-orientation="vertical"
                                                aria-labelledby="menu-button"
                                            >

                                                <div
                                                    className="py-1 rounded-md"
                                                    role="none"
                                                    style={{ backgroundColor: "white" }}
                                                >
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("pending")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Pending
                                                    </button>

                                                    <button
                                                        onClick={() => handleOrderTypeSelect("ongoing")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Ongoing
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("completed")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Completed
                                                    </button>

                                                    <button
                                                        onClick={() => handleOrderTypeSelect("shipped")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Shipped
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("cancelled")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Cancelled
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("failed")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Failed
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("processing")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                        role="menuitem"
                                                    >
                                                        Processing
                                                    </button>

                                                    <div className="relative w-full md:w-auto flex-shrink-0">
                                                        <button
                                                            type="button"
                                                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-white hover:text-darkGreen3"
                                                            onClick={() => handleOrderTypeSelect("")} // Reset filter
                                                        >
                                                            All Orders
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Start Date */}
                            <div className="min-w-[150px]">
                                <div className="relative">
                                    {/* <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                                    <input
                                        type="date"
                                        value={filters.start_date}
                                        onChange={(e) => handleFilterChange("start_date", e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        max={filters.end_date || undefined}
                                        placeholder="Start Date"
                                    />
                                </div>
                            </div>

                            {/* End Date */}
                            <div className="min-w-[150px]">
                                <div className="relative">
                                    {/* <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                                    <input
                                        type="date"
                                        value={filters.end_date}
                                        onChange={(e) => handleFilterChange("end_date", e.target.value)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-orange1 focus:border-transparent"
                                        min={filters.start_date || undefined}
                                        placeholder="End Date"
                                    />
                                </div>
                            </div>

                            {/* Clear Dates Button */}
                            {areDatesSelected && (
                                <div className="min-w-[120px]">
                                    <button
                                        onClick={handleClearDates}
                                        className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-2.5 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Clear Dates
                                    </button>
                                </div>
                            )}

                            {/* Download CSV Button */}
                            <div className="min-w-[160px]">
                                <button
                                    onClick={handleDownloadCSV}
                                    className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${areDatesSelected
                                        ? "bg-orange1 text-lGrey3 hover:bg-orange-600 cursor-pointer"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                    disabled={!areDatesSelected || loadingCSV}
                                    title={!areDatesSelected ? "Select start and end dates to enable download" : ""}
                                >
                                    {loadingCSV ? (
                                        <>
                                            <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Download CSV
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* MOBILE FILTERS (below md) - Stacked */}
                        <div className="md:hidden flex flex-col gap-3">
                            {/* Search */}
                            <div className="w-full">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="search"
                                        className="block w-full pl-10 pr-3 py-3 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                        placeholder="Search ..."
                                        value={filterText}
                                        onChange={handleFilter}
                                    />
                                </div>
                            </div>

                            {/* Status Dropdown Mobile */}
                            <div className="w-full">
                                {permissions.some(permission => permission.name === 'order.view') && (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            className="w-full inline-flex justify-between items-center gap-x-2 rounded-md px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300"
                                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        >
                                            {selectedOrderType ? formatPermissionText(selectedOrderType) : "All Status"}
                                            <svg
                                                className="-mr-1 h-5 w-5 text-gray-400"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                        {isDropdownOpen && (
                                            <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        All Orders
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("pending")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Pending
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("ongoing")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Ongoing
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("completed")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Completed
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("shipped")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Shipped
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("cancelled")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Cancelled
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("failed")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Failed
                                                    </button>
                                                    <button
                                                        onClick={() => handleOrderTypeSelect("processing")}
                                                        className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Processing
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Dates and buttons in a grid */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Start Date */}
                                <div className="col-span-1">
                                    <div className="relative">
                                        {/* <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                                        <input
                                            type="date"
                                            value={filters.start_date}
                                            onChange={(e) => handleFilterChange("start_date", e.target.value)}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                                            placeholder="Start"
                                        />
                                    </div>
                                </div>

                                {/* End Date */}
                                <div className="col-span-1">
                                    <div className="relative">
                                        {/* <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" /> */}
                                        <input
                                            type="date"
                                            value={filters.end_date}
                                            onChange={(e) => handleFilterChange("end_date", e.target.value)}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50"
                                            placeholder="End"
                                        />
                                    </div>
                                </div>

                                {/* Clear Dates Button */}
                                {areDatesSelected && (
                                    <div className="col-span-2">
                                        <button
                                            onClick={handleClearDates}
                                            className="w-full inline-flex justify-center items-center gap-x-2 px-4 py-3 text-sm font-medium text-lGrey bg-black1 rounded-lg hover:bg-gray-800"
                                        >
                                            <XCircle className="w-4 h-4" />
                                            Clear Dates
                                        </button>
                                    </div>
                                )}

                                {/* Download Button */}
                                <div className="col-span-2">
                                    <button
                                        onClick={handleDownloadCSV}
                                        className={`w-full inline-flex justify-center items-center gap-x-2 rounded-md px-4 py-3 text-sm font-semibold ${areDatesSelected
                                            ? "bg-orange1 text-lGrey3 hover:bg-orange-600"
                                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            }`}
                                        disabled={!areDatesSelected || loadingCSV}
                                    >
                                        {loadingCSV ? (
                                            <>
                                                <ArrowClockwise className="w-4 h-4 text-white animate-spin" />
                                                <span className="ml-2">Downloading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4" />
                                                <span className="ml-2">Download CSV</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* DESKTOP help text */}
                        <div className="hidden md:flex items-center justify-between mt-2">
                            {!areDatesSelected && (
                                <p className="text-xs text-gray-500">
                                    Select start and end dates to enable download
                                </p>
                            )}
                            {areDatesSelected && sortedData.length > 0 && (
                                <p className="text-sm text-gray-600">
                                    Showing {sortedData.length} records from {filters.start_date} to {filters.end_date}
                                </p>
                            )}
                        </div>

                        {/* MOBILE help text */}
                        <div className="md:hidden mt-3">
                            {!areDatesSelected && (
                                <p className="text-xs text-gray-500 text-center mb-2">
                                    Select start and end dates to enable download
                                </p>
                            )}
                            {areDatesSelected && sortedData.length > 0 && (
                                <p className="text-sm text-gray-600 text-center">
                                    Showing {sortedData.length} records
                                </p>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={sortedData}
                            pagination
                            paginationServer
                            paginationRowsPerPageOptions={[100]}
                            paginationTotalRows={total}
                            paginationDefaultPage={page}
                            paginationPerPage={perPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            progressPending={loading}
                            progressComponent={<Spinner loading={loading} />}
                            highlightOnHover
                            noDataComponent={<div>No order found</div>}
                            expandableRows
                            expandableRowsComponent={ExpandableComponent}
                            customStyles={CustomStyles}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default GetAllOrders;